import React, { useRef, useState, useEffect } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import './CertificateGenerator.css';
import logo from '../../assets/logo.png';
import sealImage from '../../assets/certified.png';
import { toast, ToastContainer } from 'react-toastify';
import { QRCodeCanvas } from 'qrcode.react';
import api from '../../api';
import 'react-toastify/dist/ReactToastify.css';

const CertificateGenerator = () => {
  const { courseId } = useParams();
  const certificateRef = useRef();
  const pdfCertificateRef = useRef(); // Hidden desktop-size cert
  const [course, setCourse] = useState(null);
  const { profile, token } = useAuth();
  const [dateIssued, setDateIssued] = useState(new Date().toLocaleDateString());
  const [downloaded, setDownloaded] = useState(false);
  const [certificateId, setCertificateId] = useState(null);
  const [certificateNumber, setCertificateNumber] = useState(null);
  const navigate = useNavigate();
  const [showQRMenu, setShowQRMenu] = useState(false);
  const [showShareOptions, setShowShareOptions] = useState(false);    
  const frontendurl = window.location.href.split('/').slice(0, 3).join('/');
  const certificateUrl = `${frontendurl}/publicCertificateView/${certificateNumber}`;



  useEffect(() => {
    if (!profile) return;
    const localKey = `certificateNumber_${profile._id}_${courseId}`;
    let storedNumber = localStorage.getItem(localKey);
    if (!storedNumber) {
      storedNumber = `CERT-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
      localStorage.setItem(localKey, storedNumber);
    }
    setCertificateNumber(storedNumber);

    const fetchCourseDetails = async () => {
      try {
        const response = await axios.get(api.getCourseById(courseId), {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCourse(response.data.course);

        const certificateResponse = await axios.get(api.getCertificatesByUser(profile._id));
        const existingCertificate = certificateResponse.data.certificates.find(
          cert => cert.courseId._id === courseId,
        );
        if (existingCertificate) {
          setDownloaded(existingCertificate.downloaded);
          setCertificateId(existingCertificate._id);
          setCertificateNumber(existingCertificate.certificateNumber);
          localStorage.setItem(localKey, existingCertificate.certificateNumber);
        }
      } catch (error) {
        console.error('Error fetching course details:', error);
      }
    };

    fetchCourseDetails();
  }, [courseId, profile?._id, token]);

  if (!profile) {
    return <div>Loading...</div>;
  }

  const studentName = profile?.name || 'Student Name';
  const courseTitle = course?.title || `Course #${courseId}`;
  const instructorName = course?.instructor || 'Instructor Name';
  const courseDuration = course?.duration || 'Unknown Duration';

  const generatePDF = async () => {
    const saveButton = document.querySelector('.ccp-download-btn');
    if (saveButton) saveButton.style.display = 'none';

    // Use the hidden desktop certificate for PDF
    const canvas = await html2canvas(pdfCertificateRef.current, {
      ignoreElements: (el) => el.classList.contains('ccp-download-btn'),
      backgroundColor: '#fff',
      scale: 2, // for better quality
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('landscape', 'pt', 'a4');
    pdf.addImage(imgData, 'PNG', 20, 20, 800, 560);

    const formData = new FormData();
    const pdfBlob = new Blob([pdf.output('arraybuffer')], { type: 'application/pdf' });
    formData.append('pdfFile', pdfBlob, 'certificate.pdf');
    formData.append('userId', profile._id);
    formData.append('courseId', courseId);
    formData.append('certificateNumber', certificateNumber);

    try {
      const response = await axios.post(api.createCertificate, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      setDownloaded(true);
      setCertificateId(response.data.certificate._id);
      toast.success('Certificate has been saved to My Certificates!');

      // setTimeout(() => {
      //     navigate('/my-certificates');
      // }, 3000);
    } catch (error) {
      console.error('Error saving certificate:', error.response?.data || error);
      toast.error('Error saving certificate!');
    } finally {
      if (saveButton) saveButton.style.display = 'block';
    }
  };

  const handleRedirect = () => navigate('/my-certificates');
  const handleGoBack = () => navigate(-1);



  const handleDownloadQR = () => {
    const qrCanvas = document.getElementById('qr-code-canvas');
    if (qrCanvas) {
      qrCanvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'certificate-qr.png';
        link.click();
      }, 'image/png');
    }
  };

  return (
    <div className="ccp-certificate-wrapper">
      <div className='ccp-spcv-container'>
        <div className="pcv-header">
          <h1 className="ccp-title">Certificate</h1>
          <div className='ccp-button-group'>
            {!downloaded ? (
              <button className="ccp-download-btn" onClick={generatePDF}>
                                Save Certificate to My Certificates
              </button>
            ) : (
              <>
                <button className="ccp-go-to-my-certificates-btn" onClick={handleRedirect}>
                                    My Certificates
                </button>

                {/* 🔽 New QR Button with Dropdown */}
                <div className="ccp-qr-dropdown-wrapper">
                  <button
                    className="ccp-download-qr-btn"
                    onClick={() => {
                      setShowQRMenu((prev) => !prev);
                      setShowShareOptions(false);
                    }}
                  >
    Share
                  </button>

                  {/* Dropdown Menu */}
                  {showQRMenu && (
                    <div className="ccp-qr-dropdown">
                      <button onClick={handleDownloadQR} title="Download the QR code as an image to scan and view the certificate">
      Download QR Code
                      </button>

                      <button
                        title="Share your certificate via WhatsApp or Email as a link"
                        onClick={() => {
                          setShowShareOptions((prev) => !prev);
                          setShowQRMenu(false); // Close main QR menu
                        }}
                      >
       Share Certificate
                      </button>

                    </div>
                  )}

                  {/* Share Options */}
                  {showShareOptions && (
                    <div className="ccp-qr-share-options">
                      <button
                        onClick={() => {
                          window.open(`https://wa.me/?text=Here is your certificate: ${certificateUrl}`);
                          setShowShareOptions(false);
                        }}
                      >
        Share via WhatsApp
                      </button>
                      <button
                        onClick={() => {
                          window.open(
                            `https://mail.google.com/mail/?view=cm&fs=1&to=&su=Check%20out%20my%20Certificate&body=View%20my%20Certificate:%20${encodeURIComponent(certificateUrl)}`,
                          );
                          setShowShareOptions(false);
                        }}
                      >
        Share via Email
                      </button>
                    </div>
                  )}
                </div>




                <button onClick={handleGoBack} className="ccp-btn-button-back">
                                    Go Back
                </button>
              </>
            )}

                     
          </div>
        </div>
      </div>

      <div ref={certificateRef} className="ccp-certificate">
        <ToastContainer />
        <div className="ccp-certificate-header">
          <img src={logo} alt="Course Logo" className="ccp-logo" />
          <h1 className="ccp-certificate-title">Certificate of Completion</h1>
        </div>
        <div className="ccp-certificate-body">
          <p className="ccp-text-1">This certifies that</p>
          <h2 className="ccp-student-name">{studentName}</h2>
          <p className="ccp-text-2">has successfully completed the course.</p>
          <h3 className="ccp-course-title">{courseTitle}</h3>

          <div className="ccp-certificate-info">
            <span className="label">Certificate Number:</span>
            <span className="value" style={{ fontWeight: 'bold', marginLeft: 8 }}>{certificateNumber}</span>
          </div>

          <div className="ccp-details" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 30, fontSize: 18, color: '#6b7280' }}>
            <p className="ccp-instructor" style={{ marginBottom: 0, textAlign: 'center' }}>Instructor: {instructorName}</p>
            <div style={{ display: 'flex', flexDirection: 'row', gap: '30px', justifyContent: 'center', alignItems: 'center', marginTop: 8 }}>
              <p className="ccp-duration" style={{ marginBottom: 0 }}>Course Duration: {Math.floor(course?.totalHours || 0)}h {Math.round((course?.totalHours || 0) * 60) % 60}m</p>
              <p className="ccp-date" style={{ marginBottom: 0 }}>Date Issued: {dateIssued}</p>
            </div>
          </div>

          <div className="ccp-seal">
            <img src={sealImage} alt="Certified Seal" className="ccp-seal-image" />
          </div>

          <div className="ccp-qr">
            <QRCodeCanvas id="qr-code-canvas" value={certificateUrl} size={100} />
          </div>
        </div>
      </div>

      {/* Hidden fixed-size desktop certificate for PDF export */}
      <div
        ref={pdfCertificateRef}
        className="ccp-certificate"
        style={{
          width: '900px',
          height: '600px',
          position: 'absolute',
          left: '-9999px',
          top: 0,
          overflow: 'hidden',
          background: '#fff',
          zIndex: -1000,
        }}
        aria-hidden="true"
      >
        <div className="ccp-certificate-header">
          <img src={logo} alt="Course Logo" className="ccp-logo" />
          <h1 className="ccp-certificate-title">Certificate of Completion</h1>
        </div>
        <div className="ccp-certificate-body">
          <p className="ccp-text-1">This certifies that</p>
          <h2 className="ccp-student-name">{studentName}</h2>
          <p className="ccp-text-2">has successfully completed the course.</p>
          <h3 className="ccp-course-title">{courseTitle}</h3>

          <div className="ccp-certificate-info">
            <span className="label">Certificate Number:</span>
            <span className="value" style={{ fontWeight: 'bold', marginLeft: 8 }}>{certificateNumber}</span>
          </div>

          <div className="ccp-details" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 30, fontSize: 18, color: '#6b7280' }}>
            <p className="ccp-instructor" style={{ marginBottom: 0, textAlign: 'center' }}>Instructor: {instructorName}</p>
            <div style={{ display: 'flex', flexDirection: 'row', gap: '30px', justifyContent: 'center', alignItems: 'center', marginTop: 8 }}>
              <p className="ccp-duration" style={{ marginBottom: 0 }}>Course Duration: {Math.floor(course?.totalHours || 0)}h {Math.round((course?.totalHours || 0) * 60) % 60}m</p>
              <p className="ccp-date" style={{ marginBottom: 0 }}>Date Issued: {dateIssued}</p>
            </div>
          </div>

          <div className="ccp-seal" style={{ width: '200px', height: '200px', left: '30px', bottom: '70px', position: 'absolute', background: '#fff', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <img src={sealImage} alt="Certified Seal" className="ccp-seal-image" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          </div>

          <div className="ccp-qr">
            <QRCodeCanvas value={certificateUrl} size={100} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CertificateGenerator;
